import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './context/AuthContext'

// Pages
import Login from './pages/Login'
import AdminDashboard from './pages/AdminDashboard'
import ClientDashboard from './pages/ClientDashboard'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsAndConditions from './pages/TermsAndConditions'

// Components
import ProtectedRoute from './components/common/ProtectedRoute'
import SitePreview from './components/client/SitePreview'
import LoadingSpinner from './components/common/LoadingSpinner'
import { ThemeProvider } from './components/ui/theme-provider'
import { Toaster } from './components/ui/toaster'
import { Button } from './components/ui/button'
import { BrandingProvider } from './context/BrandingContext'

const fullPageCenter = {
  minHeight: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg-0)',
}

function App() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <ThemeProvider defaultTheme="system" storageKey="webnest-ui-theme">
        <BrandingProvider>
          <div style={fullPageCenter}>
            <LoadingSpinner size="lg" />
          </div>
        </BrandingProvider>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider defaultTheme="system" storageKey="webnest-ui-theme">
      <BrandingProvider>
      <div style={{ minHeight: '100dvh', background: 'var(--bg-0)' }}>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to={user.role === 'admin' ? '/admin' : '/client'} replace />
              ) : (
                <Login />
              )
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Client site preview — full-page, no sidebar */}
          <Route
            path="/client/preview/:siteId"
            element={
              <ProtectedRoute requiredRole="client">
                <SitePreview />
              </ProtectedRoute>
            }
          />

          {/* Client routes */}
          <Route
            path="/client/*"
            element={
              <ProtectedRoute requiredRole="client">
                <ClientDashboard />
              </ProtectedRoute>
            }
          />

          {/* Legal pages — public, no auth required */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />

          {/* Default redirect */}
          <Route
            path="/"
            element={
              <Navigate
                to={
                  user
                    ? user.role === 'admin'
                      ? '/admin'
                      : '/client'
                    : '/login'
                }
                replace
              />
            }
          />

          {/* 404 fallback */}
          <Route
            path="*"
            element={
              <div style={fullPageCenter}>
                <div style={{ textAlign: 'center' }}>
                  <h1 style={{ fontSize: '4rem', fontWeight: 700, color: 'var(--text-faint)', margin: '0 0 0.5rem' }}>404</h1>
                  <p style={{ fontSize: '1.125rem', color: 'var(--text-soft)', margin: '0 0 0.25rem' }}>Page not found</p>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: '0 0 1.5rem' }}>
                    The page you're looking for doesn't exist.
                  </p>
                  <Button
                    onClick={() => window.history.back()}
                    variant="outline"
                  >
                    Go Back
                  </Button>
                </div>
              </div>
            }
          />
        </Routes>
        <Toaster />
      </div>
      </BrandingProvider>
    </ThemeProvider>
  )
}

export default App
